import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Activity, 
  Search, 
  Filter, 
  Clock, 
  User, 
  Calendar,
  LogIn,
  FileText,
  CheckCircle,
  MessageSquare,
  Eye,
  Settings,
  RefreshCw,
  Download
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Action type icons and colors
const actionConfig = {
  login: { icon: LogIn, color: "text-[#2563EB]", bg: "bg-[#2563EB]/10", label: "Login" },
  logout: { icon: LogIn, color: "text-[#6B7280]", bg: "bg-[#6B7280]/10", label: "Logout" },
  session_created: { icon: FileText, color: "text-[#8B5CF6]", bg: "bg-[#8B5CF6]/10", label: "Session Created" },
  session_started: { icon: Activity, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", label: "Session Started" },
  session_completed: { icon: CheckCircle, color: "text-[#059669]", bg: "bg-[#059669]/10", label: "Session Completed" },
  qa_answered: { icon: MessageSquare, color: "text-[#EC4899]", bg: "bg-[#EC4899]/10", label: "Q&A Answered" },
  feedback_viewed: { icon: Eye, color: "text-[#14B8A6]", bg: "bg-[#14B8A6]/10", label: "Feedback Viewed" },
  profile_updated: { icon: Settings, color: "text-[#6366F1]", bg: "bg-[#6366F1]/10", label: "Profile Updated" },
  default: { icon: Activity, color: "text-[#5C5C5C]", bg: "bg-[#5C5C5C]/10", label: "Activity" }
};

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [filterAction, setFilterAction] = useState("all");

  useEffect(() => {
    fetchData();
  }, [filterDepartment, filterUser, filterAction]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (filterDepartment !== "all") params.append("department", filterDepartment);
      if (filterUser !== "all") params.append("user_id", filterUser);
      if (filterAction !== "all") params.append("action_type", filterAction);
      params.append("limit", "200");

      const [logsRes, summaryRes, usersRes] = await Promise.all([
        axios.get(`${API}/activity-logs?${params.toString()}`),
        axios.get(`${API}/activity-logs/summary`),
        axios.get(`${API}/users`)
      ]);

      setLogs(logsRes.data.logs || []);
      setSummary(summaryRes.data);
      setUsers(usersRes.data || []);
      
      // Extract unique departments
      const depts = [...new Set(usersRes.data.map(u => u.department))].filter(Boolean);
      setDepartments(depts);
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
      toast.error("Failed to load activity logs");
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getActionConfig = (actionType) => {
    return actionConfig[actionType] || actionConfig.default;
  };

  // Filter logs by search
  const filteredLogs = logs.filter(log =>
    log.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Export to CSV
  const exportToCSV = () => {
    if (filteredLogs.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = ["Timestamp", "User", "Department", "Action", "Description"];
    const csvRows = [
      headers.join(","),
      ...filteredLogs.map(log => [
        new Date(log.timestamp).toLocaleString(),
        `"${log.user_name || ''}"`,
        `"${log.department || ''}"`,
        `"${log.action_type || ''}"`,
        `"${log.description || ''}"`
      ].join(","))
    ];

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Activity log exported!");
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Activity Log</h1>
          <p className="text-[#5C5C5C]">Track all user and admin activities</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="rounded-full">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={exportToCSV} className="rounded-full bg-primary">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <Activity className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.recent_activity_count || 0}</p>
                  <p className="text-sm text-[#5C5C5C]">Last 24 Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-[#059669]/10">
                  <CheckCircle className="w-5 h-5 text-[#059669]" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.action_counts?.session_completed || 0}</p>
                  <p className="text-sm text-[#5C5C5C]">Sessions Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-[#2563EB]/10">
                  <LogIn className="w-5 h-5 text-[#2563EB]" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.action_counts?.login || 0}</p>
                  <p className="text-sm text-[#5C5C5C]">Total Logins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-[#8B5CF6]/10">
                  <FileText className="w-5 h-5 text-[#8B5CF6]" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.action_counts?.session_created || 0}</p>
                  <p className="text-sm text-[#5C5C5C]">Sessions Created</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="stats-card">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Filter className="w-5 h-5 text-primary" />
                Filter Activities
              </CardTitle>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search activities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="User" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Action Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="session_created">Session Created</SelectItem>
                  <SelectItem value="session_completed">Session Completed</SelectItem>
                  <SelectItem value="qa_answered">Q&A Answered</SelectItem>
                  <SelectItem value="feedback_viewed">Feedback Viewed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Activity Timeline */}
      <Card className="stats-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Activity Timeline ({filteredLogs.length} activities)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredLogs.length > 0 ? (
            <div className="space-y-1">
              {filteredLogs.map((log, index) => {
                const config = getActionConfig(log.action_type);
                const Icon = config.icon;
                
                return (
                  <div
                    key={log.id || index}
                    className="flex items-start gap-4 p-4 hover:bg-[#F3F4F6] rounded-lg transition-colors"
                  >
                    {/* Icon */}
                    <div className={`p-2 rounded-full ${config.bg} flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[#1A1A1A]">
                          {log.user_name || "Unknown User"}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>
                          {config.label}
                        </span>
                        {log.department && (
                          <span className="text-xs text-[#5C5C5C] bg-[#F3F4F6] px-2 py-0.5 rounded-full">
                            {log.department}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#5C5C5C] mt-1 truncate">
                        {log.description}
                      </p>
                    </div>
                    
                    {/* Timestamp */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm text-[#5C5C5C]">{formatTimestamp(log.timestamp)}</p>
                      <p className="text-xs text-[#9CA3AF]">
                        {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
              <p className="text-[#5C5C5C]">No activities found</p>
              <p className="text-sm text-[#9CA3AF]">Activities will appear here as users interact with the platform</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Most Active Users */}
      {summary?.most_active_users?.length > 0 && (
        <Card className="stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Most Active Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {summary.most_active_users.map((user, index) => (
                <div key={user.user_id} className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    index === 0 ? 'bg-[#F59E0B] text-white' :
                    index === 1 ? 'bg-[#9CA3AF] text-white' :
                    index === 2 ? 'bg-[#CD7F32] text-white' :
                    'bg-[#F3F4F6] text-[#5C5C5C]'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-[#1A1A1A]">{user.user_name || "Unknown"}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">{user.count}</p>
                    <p className="text-xs text-[#5C5C5C]">activities</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
